import { useState } from 'react'
import { AlertTriangle, Copy, KeyRound, Loader2, Plus } from 'lucide-react'
import { ErpTable, Panel, StatusBadge, Td, Th } from '@/components/erp/primitives'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan, useSession } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type { CreateApiKeyResponse } from '@/shared/api/types'
import { cn } from '@/lib/utils'
import { createApiKey, listApiKeys, revokeApiKey } from './api'
import { ApiKeyDrawer, expiryTone } from './ApiKeyDrawer'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * API keys we issue.
 *
 * The whole design of this tab turns on one fact: **the key is returned exactly
 * once, in the creation response, and is never retrievable again.** There is no
 * "show key" endpoint and no support path that recovers it. So the creation flow
 * ends in a modal that says so in plain words and keeps the value on screen until
 * the operator dismisses it deliberately — anything subtler and people lose keys.
 */
export function ApiKeysTab() {
  const canCreate = useCan('apikey:create')
  const canRevoke = useCan('apikey:revoke')

  const keys = useLoad(() => listApiKeys(), [])
  const [createOpen, setCreateOpen] = useState(false)
  const [issued, setIssued] = useState<CreateApiKeyResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [openKeyId, setOpenKeyId] = useState<string | null>(null)

  const rows = keys.data ?? []

  return (
    <>
      <Panel bodyClassName="p-0">
        <ErrorStrip message={keys.error ?? error} />
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-3">
          <span className="text-erp-xs text-muted-foreground">
            Keys authenticate as a machine principal with their own authorities and geographic
            scope. A key can never exceed the authorities of whoever created it.
          </span>
          {canCreate && (
            <button type="button" className="erp-btn shrink-0" onClick={() => setCreateOpen(true)} aria-label="New key">
              <Plus className="h-3.5 w-3.5" /> <span className="axis-hide-sm">New key</span>
            </button>
          )}
        </div>

        <ErpTable head={<>
          <Th>Name</Th>
          <Th>Prefix</Th>
          <Th>Scope</Th>
          <Th>Status</Th>
          <Th className="axis-hide-md">Expires</Th>
          <Th className="axis-hide-lg">Last used</Th>
          <Th />
        </>}>
          {keys.loading && <LoadingRows colSpan={7} />}
          {!keys.loading && rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
                No API keys issued.
              </td>
            </tr>
          )}
          {!keys.loading && rows.map((k) => (
            <tr
              key={k.id}
              className="hover:bg-muted/40 cursor-pointer"
              onClick={() => setOpenKeyId(k.id)}
              tabIndex={0}
              role="button"
              aria-label={`Open ${k.name}`}
              onKeyDown={(e) => {
                // Only when the row itself has focus. Otherwise Enter on a
                // nested button both activates it and opens the drawer, and
                // Space is swallowed by preventDefault so the button never fires.
                if (e.target !== e.currentTarget) return
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenKeyId(k.id) }
              }}
            >
              <Td className="font-medium">{k.name}</Td>
              <Td className="font-mono text-erp-xs">{k.prefix}…</Td>
              <Td className="text-erp-xs">{k.scopeLevel}</Td>
              <Td><StatusBadge status={k.status.toLowerCase()} /></Td>
              <Td className={cn('axis-hide-md', expiryTone(k.expiresAt))}>{fmtDateTime(k.expiresAt)}</Td>
              <Td className="axis-hide-lg">
                {k.lastUsedAt ? fmtDateTime(k.lastUsedAt) : <span className="text-muted-foreground">Never</span>}
              </Td>
              <Td className="text-right">
                {canRevoke && k.status === 'ACTIVE' && (
                  <button
                    type="button"
                    className="erp-btn"
                    onClick={async (e) => {
                      e.stopPropagation()
                      setError(null)
                      try {
                        await revokeApiKey(k.id, 'Revoked from the console')
                        await keys.refresh()
                      } catch (err) { setError(explain(err)) }
                    }}
                  >
                    Revoke
                  </button>
                )}
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>

      <CreateKeyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (result) => {
          setCreateOpen(false)
          setIssued(result)
          await keys.refresh()
        }}
      />

      <IssuedKeyModal issued={issued} onClose={() => setIssued(null)} />

      <ApiKeyDrawer keyId={openKeyId} onClose={() => setOpenKeyId(null)} />
    </>
  )
}

function CreateKeyModal({
  open, onClose, onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (result: CreateApiKeyResponse) => void | Promise<void>
}) {
  const authorities = useSession((s) => s.authorities)
  const [name, setName] = useState('')
  const [scopeLevel, setScopeLevel] = useState('NATIONAL')
  const [expiresAt, setExpiresAt] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A key can never exceed its creator, and never holds the wildcard, so the
  // picker offers exactly what the caller could delegate and nothing more.
  const grantable = (authorities ?? []).filter((a) => a !== '*:*').sort()

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Issue an API key"
      description="Shown once at creation and never again. The key inherits at most your own authorities."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={!name.trim() || !expiresAt || saving}
            onClick={async () => {
              setSaving(true)
              setError(null)
              try {
                const result = await createApiKey({
                  name: name.trim(),
                  authorities: picked,
                  scopeLevel,
                  // The backend caps the lifetime; a key with no expiry is an
                  // incident with a date not yet assigned, so the field is required here.
                  expiresAt: new Date(`${expiresAt}T00:00:00Z`).toISOString(),
                })
                await onCreated(result)
                setName(''); setPicked([]); setExpiresAt('')
              } catch (err) {
                setError(explain(err))
              } finally {
                setSaving(false)
              }
            }}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
            Issue
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <Note tone="bad">{error}</Note>}
        <label className="block">
          <span className="text-erp-xs text-muted-foreground">Name</span>
          <input
            className="erp-input w-full mt-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Mobile client, tally partner feed…"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-erp-xs text-muted-foreground">Scope level</span>
            <select className="erp-input w-full mt-1" value={scopeLevel} onChange={(e) => setScopeLevel(e.target.value)}>
              {['NATIONAL', 'COUNTY', 'CONSTITUENCY', 'WARD', 'STATION'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-erp-xs text-muted-foreground">Expires</span>
            <DatePicker value={expiresAt} onChange={setExpiresAt} className="w-full mt-1" />
          </label>
        </div>
        <div>
          <span className="text-erp-xs text-muted-foreground">Authorities</span>
          <div className="mt-1 max-h-48 overflow-y-auto erp-card p-2 space-y-0.5">
            {grantable.length === 0 && (
              <div className="text-erp-xs text-muted-foreground px-1 py-2">
                You hold no delegable authorities.
              </div>
            )}
            {grantable.map((a) => (
              <label key={a} className="flex items-center gap-2 text-erp-sm px-1 py-0.5 hover:bg-muted rounded">
                <input
                  type="checkbox"
                  checked={picked.includes(a)}
                  onChange={() => setPicked((p) => p.includes(a) ? p.filter((x) => x !== a) : [...p, a])}
                />
                <code className="font-mono text-erp-xs">{a}</code>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}

/** The one screen in the app that displays secret material. */
function IssuedKeyModal({
  issued, onClose,
}: { issued: CreateApiKeyResponse | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <Modal
      open={Boolean(issued)}
      onClose={onClose}
      title="Copy this key now"
      footer={<button type="button" className="erp-btn erp-btn-primary" onClick={onClose}>I have saved it</button>}
    >
      <div className="space-y-3">
        <Note tone="bad">
          <span className="inline-flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              This is the only time this key will ever be shown. It is stored hashed —
              nobody, including an administrator, can recover it. If you lose it, revoke
              the key and issue another.
            </span>
          </span>
        </Note>
        <div className="erp-card p-3 font-mono text-erp-sm break-all select-all">
          {issued?.key}
        </div>
        <button
          type="button"
          className="erp-btn"
          onClick={async () => {
            if (!issued) return
            try {
              await navigator.clipboard.writeText(issued.key)
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            } catch { /* clipboard blocked — the value is selectable above */ }
          }}
        >
          <Copy className="h-3.5 w-3.5" /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </Modal>
  )
}
